import React, { createContext, useContext, useMemo } from 'react'

import { getUserByEmail } from 'components/rbac-settings/settings-utils'
import useAuth from 'hooks/use-auth'
import { useGetCompanyUsers } from 'hooks/use-get-company-users'
import { useGetCompanyWithoutTeams } from 'queries/companies/use-get-company-without-teams'
import { companiesIdGet } from 'utils/endpoints-access'

import type { Company } from 'types/api/rbac/company'

interface SettingsQueryContextValue {
  company?: Company
  queryCompanyResult: ReturnType<typeof useGetCompanyWithoutTeams>
  queryUsersResult: ReturnType<typeof useGetCompanyUsers>
  user?: ReturnType<typeof getUserByEmail>
}

export const SettingsQueryContext = createContext<
  SettingsQueryContextValue | undefined
>(undefined)

export const SettingsQueryContextProvider: React.FC = ({ children }) => {
  const { hasEndpointAccess, session } = useAuth()
  const { identifier } = session
  const canAccessCompanies = hasEndpointAccess(companiesIdGet)
  const queryCompanyResult = useGetCompanyWithoutTeams({
    enabled: canAccessCompanies
  })
  const queryUsersResult = useGetCompanyUsers() // note: privilege access is built into this hook

  const queryContextValue = useMemo<SettingsQueryContextValue>(
    () => ({
      company: queryCompanyResult.data,
      queryCompanyResult,
      queryUsersResult,
      user: getUserByEmail(identifier, queryUsersResult.data)
    }),
    [identifier, queryCompanyResult, queryUsersResult]
  )

  return (
    <SettingsQueryContext.Provider value={queryContextValue}>
      {children}
    </SettingsQueryContext.Provider>
  )
}

export function useSettingsQueryContext() {
  const context = useContext(SettingsQueryContext)

  if (!context) {
    throw new Error(
      'useSettingsQueryContext must be used within its respective provider'
    )
  }

  return context
}
